/**
 * Content Tracker calendar (US-07 month view, US-23 backlog onto the calendar). Each item sits on its working date. Drag an
 * item onto a day of the visible month to reschedule it (native HTML5 drag and drop, no library). Every item also has a
 * "Move to date" control for the keyboard. The original date stays (US-19). Undated items and the open backlog sit in
 * side panels. Every move is one reschedule call; the server decides what is allowed (NFR-05).
 */
import './content.css';
import { useCallback, useId, useState, type DragEvent, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router';
import { Button, Heading, Text } from '@qm/ui';
import type { DeliverableView, TrackerMe } from '@qm/contracts/content';
import { useSurface } from '../kit/context';
import { useAction, useResource, type Resource } from '../kit/resource';
import { Loadable, Notice, Screen } from '../kit/ui';
import { deliverableQuery, type ContentApi, type PagedList } from '../api/content';
import {
  ContentError, ContentNav, FilterBar, StatusBadge, WithBase, fmtDate, fmtMonth, filtersFromParams, listHref, setParam, shiftDay, shiftMonth, statusTone,
  useApi, usePoll, weekdayOf, type Base, type Filters,
} from './content';

/** The month on screen (YYYY-MM). The month filter is not shown here, so a link that sets it still opens the right month. */
export const CALENDAR_PARAM = 'cal';
/** Custom drag type for item ids. text/plain is set too, because Firefox only starts a drag when some data is set. */
export const DRAG_TYPE = 'application/x-qm-content-item';
const GRID_WEEKS = 6;
const DAY_LIMIT = 4;
const BACKLOG_LIMIT = 200;
const MONTH_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

// ---------------------------------------------------------------- pure helpers (exported for tests)

/** The month to show: the URL's month when it is a real one, else the agency's current month (me.today). */
export function calendarMonth(raw: string | null | undefined, today: string): string {
  return raw && MONTH_RE.test(raw) ? raw : today.slice(0, 7);
}

/** Six Monday-first weeks around a month: every day in the grid, and its first and last day. */
export function monthGrid(month: string): { from: string; to: string; days: string[] } {
  const first = `${month}-01`;
  const dow = new Date(`${first}T00:00:00Z`).getUTCDay(); // 0 = Sunday
  const from = shiftDay(first, -((dow + 6) % 7));
  const days = Array.from({ length: GRID_WEEKS * 7 }, (_, i) => shiftDay(from, i));
  return { from, to: days[days.length - 1]!, days };
}

/** The filters the calendar shares with the list. The month and the date range come from the calendar itself. */
export function calendarFilters(filters: Filters): Filters {
  const out: Filters = { ...filters };
  delete out.month;
  delete out.from;
  delete out.to;
  return out;
}

export type CalendarView = 'month' | 'week' | 'day' | 'list';
const CALENDAR_VIEWS: readonly CalendarView[] = ['month', 'week', 'day', 'list'];
const FOCUS_PARAM = 'focus';
const VIEW_PARAM = 'view';
const DATE_RE = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;

export const calendarView = (raw: string | null): CalendarView => CALENDAR_VIEWS.find((v) => v === raw) ?? 'month';
/** Reject impossible dates and cross-month URL values before using them in the day grid. */
export function calendarFocus(raw: string | null, month: string, today: string): string {
  const fallback = today.startsWith(month + '-') ? today : month + '-01';
  if (!raw || !DATE_RE.test(raw) || !raw.startsWith(month + '-')) return fallback;
  const value = new Date(raw + 'T00:00:00Z');
  return !Number.isNaN(value.getTime()) && value.toISOString().slice(0, 10) === raw ? raw : fallback;
}
export function calendarVisibleDays(view: CalendarView, grid: ReturnType<typeof monthGrid>, focus: string, month: string): string[] {
  if (view === 'month') return grid.days;
  if (view === 'day') return [focus];
  if (view === 'list') return grid.days.filter((date) => date.startsWith(month + '-'));
  const first = Math.floor(Math.max(grid.days.indexOf(focus), 0) / 7) * 7;
  return grid.days.slice(first, first + 7);
}

/** Cache key for one calendar query; the URL filters are part of it, so a filter change loads again. */
export const calendarKey = (kind: 'dated' | 'undated' | 'backlog', month: string, filters: Filters) =>
  `content:calendar:${kind}:${month}:${listHref(calendarFilters(filters))}`;

/** Dated items grouped by working date. Undated items have no key and are left out. */
export function itemsByDay(items: readonly DeliverableView[]): Map<string, DeliverableView[]> {
  const out = new Map<string, DeliverableView[]>();
  for (const item of items) {
    if (!item.workingDate) continue;
    out.set(item.workingDate, [...(out.get(item.workingDate) ?? []), item]);
  }
  return out;
}

/** The reschedule for an item moved to a day, or null when it already sits on that day (nothing is sent). */
export function moveRequest(item: Pick<DeliverableView, 'workingDate'>, date: string): { date: string } | null {
  return item.workingDate === date ? null : { date };
}

/** Sends the reschedule. Null means no request was made. The original date is never touched (US-19). */
export function moveItem(api: ContentApi, item: DeliverableView, date: string): Promise<DeliverableView | null> {
  const req = moveRequest(item, date);
  return req ? api.reschedule(item.id, req) : Promise.resolve(null);
}

/** Who may move an item: an admin, or the account manager who owns its client. The server enforces the same rule. */
export function canReschedule(me: TrackerMe, clientAccountManagerId: string | undefined): boolean {
  if (!me.can.editDeliverables) return false;
  if (me.role === 'admin') return true;
  return me.role === 'account_manager' && me.memberId !== null && me.memberId === clientAccountManagerId;
}

const isItemDrag = (e: DragEvent) => Array.from(e.dataTransfer.types).includes(DRAG_TYPE);
type Rows = Resource<PagedList<DeliverableView[]>>;
const rowsOf = (r: Rows): DeliverableView[] => (r.status === 'ready' ? r.data.data : []);

// ---------------------------------------------------------------- page

export function ContentCalendarPage() {
  return <WithBase>{(b) => <CalendarBody base={b} />}</WithBase>;
}

function CalendarBody({ base }: { base: Base }) {
  const { t, locale } = useSurface();
  const api = useApi();
  const [sp, setSp] = useSearchParams();
  const filters = filtersFromParams(sp);
  const shared = calendarFilters(filters);
  const month = calendarMonth(sp.get(CALENDAR_PARAM) ?? sp.get('month'), base.me.today);
  const days = monthGrid(month);
  const view = calendarView(sp.get(VIEW_PARAM));
  const focus = calendarFocus(sp.get(FOCUS_PARAM), month, base.me.today);
  const visible = calendarVisibleDays(view, days, focus, month);

  const dated = useResource(calendarKey('dated', month, filters), () => api.deliverables(deliverableQuery({ ...shared, from: days.from, to: days.to }, 1000)));
  const undated = useResource(calendarKey('undated', month, filters), () => api.deliverables(deliverableQuery({ ...shared, month }, 1000)));
  const backlog = useResource(calendarKey('backlog', month, filters), () => api.deliverables(deliverableQuery({ ...shared, backlog: 'true' }, BACKLOG_LIMIT)));
  const reloadAll = useCallback(() => {
    dated.reload();
    undated.reload();
    backlog.reload();
  }, [dated.reload, undated.reload, backlog.reload]);
  usePoll(reloadAll, 5000);

  const move = useAction((item: DeliverableView, date: string) => moveItem(api, item, date));
  const [notice, setNotice] = useState('');
  const [overDay, setOverDay] = useState<string | null>(null);

  const everyItem = [...rowsOf(dated), ...rowsOf(undated), ...rowsOf(backlog)];
  const movable = (item: DeliverableView) => canReschedule(base.me, base.clients.find((c) => c.id === item.clientId)?.accountManagerId);

  /** One move, then a reload either way, so a conflict shows the item as it now is. True when nothing failed. */
  const onMove = async (item: DeliverableView, date: string): Promise<boolean> => {
    if (!moveRequest(item, date)) return true;
    const moved = await move.run(item, date);
    reloadAll();
    if (moved) setNotice(t('portal.partners.content.calendar.moved', { title: item.title, date: fmtDate(locale, date) }));
    return moved !== null;
  };

  const dragOver = (day: string) => (e: DragEvent<HTMLTableCellElement>) => {
    if (!isItemDrag(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (overDay !== day) setOverDay(day);
  };
  const dropOn = (day: string) => (e: DragEvent<HTMLTableCellElement>) => {
    if (!isItemDrag(e)) return;
    e.preventDefault();
    setOverDay(null);
    const id = e.dataTransfer.getData(DRAG_TYPE) || e.dataTransfer.getData('text/plain');
    const item = everyItem.find((i) => i.id === id);
    if (item && movable(item)) void onMove(item, day);
  };
  const dragLeave = (e: DragEvent<HTMLTableCellElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOverDay(null);
  };

  const byDay = itemsByDay(rowsOf(dated));
  const today = base.me.today;
  /** Only days of the visible month take drops; the days around it are shown dimmed. */
  const renderDay = (day: string) => {
    const inMonth = day.startsWith(`${month}-`);
    const droppable = inMonth || view !== 'month';
    const items = byDay.get(day) ?? [];
    const limit = view === 'month' ? DAY_LIMIT : items.length;
    return (
      <td key={day} data-testid="calendar-day" data-date={day} data-outside={inMonth ? undefined : 'true'}
        data-today={day === today ? 'true' : undefined} data-drop-over={droppable && overDay === day ? 'true' : undefined}
        onDragOver={droppable ? dragOver(day) : undefined} onDragLeave={droppable ? dragLeave : undefined} onDrop={droppable ? dropOn(day) : undefined}>
        <time dateTime={day} aria-current={day === today ? 'date' : undefined}>{Number(day.slice(8))}</time>
        {items.length > 0 ? (
          <ul className="qc-day-items">
            {items.slice(0, limit).map((item) => <ItemChip key={item.id} item={item} movable={movable(item)} onMove={onMove} />)}
            {items.length > limit ? (
              <li><Link className="qc-more" to={listHref({ ...shared, from: day, to: day })}>{t('portal.partners.content.calendar.more', { n: items.length - limit })}</Link></li>
            ) : null}
          </ul>
        ) : null}
      </td>
    );
  };

  // All views reuse the existing secured month fetch, filters and reschedule endpoint.
  const changeView = (v: CalendarView) => {
    const next = new URLSearchParams(sp);
    next.set(VIEW_PARAM, v);
    if (!next.has(FOCUS_PARAM)) next.set(FOCUS_PARAM, focus);
    setSp(next);
  };
  const jumpTo = (date: string) => {
    if (!DATE_RE.test(date)) return;
    const next = new URLSearchParams(sp);
    next.set(CALENDAR_PARAM, date.slice(0, 7));
    next.set(FOCUS_PARAM, date);
    setSp(next);
  };
  const go = (n: number) => {
    const next = new URLSearchParams(sp);
    if (view === 'month' || view === 'list') {
      const m = shiftMonth(month, n);
      next.set(CALENDAR_PARAM, m);
      next.set(FOCUS_PARAM, m + '-01');
    } else {
      const date = shiftDay(focus, n * (view === 'week' ? 7 : 1));
      next.set(CALENDAR_PARAM, date.slice(0, 7));
      next.set(FOCUS_PARAM, date);
    }
    setSp(next);
  };
  const goToday = () => {
    const next = new URLSearchParams(sp);
    next.delete('month');
    next.set(CALENDAR_PARAM, base.me.today.slice(0, 7));
    next.set(FOCUS_PARAM, base.me.today);
    setSp(next);
  };
  const prevLabel = view === 'week' ? 'prevWeek' : view === 'day' ? 'prevDay' : 'prev';
  const nextLabel = view === 'week' ? 'nextWeek' : view === 'day' ? 'nextDay' : 'next';
  const heading = view === 'day' ? fmtDate(locale, focus) : view === 'week'
    ? fmtDate(locale, visible[0]!) + ' – ' + fmtDate(locale, visible[visible.length - 1]!)
    : fmtMonth(locale, month, true);

  return (
    <Screen title={t('portal.partners.content.calendar.title')} subtitle={t('portal.partners.content.calendar.subtitle')} eyebrow={t('portal.partners.content.eyebrow')}
      testId="content-calendar">
      <ContentNav me={base.me} />
      <FilterBar base={base} filters={filters} hide={['month']}
        onChange={(key, value) => setSp(setParam(sp, key, value))}
        onClear={() => { const next = new URLSearchParams(); for (const key of [CALENDAR_PARAM, VIEW_PARAM, FOCUS_PARAM]) { const value = sp.get(key); if (value) next.set(key, value); } setSp(next); }} />
      <div className="qx-toolbar qc-cal-nav" role="group" aria-label={t('portal.partners.content.calendar.nav')}>
        <Button type="button" onClick={() => go(-1)}>{t('portal.partners.content.calendar.' + prevLabel)}</Button>
        <h2 className="qc-month" aria-live="polite">{heading}</h2>
        <Button type="button" onClick={() => go(1)}>{t('portal.partners.content.calendar.' + nextLabel)}</Button>
        <Button type="button" onClick={goToday}>{t('portal.partners.content.calendar.today')}</Button>
      </div>
      <div className="qc-cal-tools">
        <div role="group" aria-label={t('portal.partners.content.calendar.view.label')} className="qc-cal-views">
          {CALENDAR_VIEWS.map((v) => <Button key={v} type="button" aria-pressed={view === v} onClick={() => changeView(v)}>
            {t('portal.partners.content.calendar.view.' + v)}
          </Button>)}
        </div>
        <label className="qc-cal-jump" htmlFor="qc-cal-date">
          {t('portal.partners.content.calendar.jump')}
          <input id="qc-cal-date" type="date" value={focus} onChange={(e) => jumpTo(e.target.value)} />
        </label>
      </div>
      {!base.me.can.editDeliverables ? <Notice testId="calendar-readonly">{t('portal.partners.content.calendar.readOnly')}</Notice> : null}
      {notice ? <Notice testId="calendar-moved">{notice}</Notice> : null}
      <ContentError error={move.error} />

      <div className="qc-cal-layout">
        <div className="qc-cal-main">
          <Loadable resource={dated}>
            {(env: PagedList<DeliverableView[]>) => (
              <>
                {env.data.length < env.meta.total ? (
                  <Notice testId="calendar-truncated">{t('portal.partners.content.list.truncated', { shown: env.data.length, total: env.meta.total })}</Notice>
                ) : null}
                {view === 'list' ? (
                  <div className="qc-agenda" data-testid="calendar-list" aria-label={t('portal.partners.content.calendar.view.list')}>
                    {visible.filter((day) => (byDay.get(day)?.length ?? 0) > 0).map((day) => (
                      <section className="qc-agenda__day" key={day} data-date={day}>
                        <h3><time dateTime={day}>{fmtDate(locale, day)}</time></h3>
                        <ul className="qc-chips">{(byDay.get(day) ?? []).map((item) =>
                          <ItemChip key={item.id} item={item} movable={movable(item)} onMove={onMove} />
                        )}</ul>
                      </section>
                    ))}
                    {visible.every((day) => !(byDay.get(day)?.length)) ? (
                      <Text tone="muted">{t('portal.partners.content.calendar.list.empty')}</Text>
                    ) : null}
                  </div>
                ) : (
                  <div className="qx-table-wrap qc-cal-wrap" role="region" tabIndex={0}
                    aria-label={t('portal.partners.content.calendar.region')}>
                    <table className="qx-table qc-cal" data-view={view} data-testid="calendar-grid">
                      <caption className="qx-sr">{t('portal.partners.content.calendar.caption', { month: fmtMonth(locale, month, true) })}</caption>
                      <thead><tr>{visible.slice(0, view === 'month' ? 7 : visible.length).map((d) =>
                        <th key={d} scope="col">{weekdayOf(locale, d)}</th>
                      )}</tr></thead>
                      <tbody>{Array.from({ length: view === 'month' ? GRID_WEEKS : 1 }, (_, w) =>
                        <tr key={w}>{visible.slice(w * 7, w * 7 + (view === 'day' ? 1 : 7)).map(renderDay)}</tr>
                      )}</tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </Loadable>
        </div>

        <aside className="qc-cal-side" aria-label={t('portal.partners.content.calendar.side')}>
          <section className="qc-panel" aria-labelledby="calendar-undated" data-testid="calendar-undated">
            <Heading level={2} id="calendar-undated">{t('portal.partners.content.calendar.undated.heading')}</Heading>
            {base.me.can.editDeliverables ? <Text tone="muted">{t('portal.partners.content.calendar.undated.hint')}</Text> : null}
            <Loadable resource={undated}>
              {(env: PagedList<DeliverableView[]>) => {
                const rows = env.data.filter((i) => i.workingDate === null);
                return rows.length === 0
                  ? <Text tone="muted">{t('portal.partners.content.calendar.undated.empty')}</Text>
                  : <ul className="qc-chips">{rows.map((item) => <ItemChip key={item.id} item={item} movable={movable(item)} onMove={onMove} />)}</ul>;
              }}
            </Loadable>
          </section>

          <section className="qc-panel" aria-labelledby="calendar-backlog" data-testid="calendar-backlog">
            <Heading level={2} id="calendar-backlog">{t('portal.partners.content.calendar.backlog.heading')}</Heading>
            {base.me.can.editDeliverables ? <Text tone="muted">{t('portal.partners.content.calendar.backlog.hint')}</Text> : null}
            <Loadable resource={backlog}>
              {(env: PagedList<DeliverableView[]>) => (
                <>
                  <Text tone="muted" data-testid="calendar-backlog-count">{t('portal.partners.content.calendar.backlog.open', { n: env.meta.total })}</Text>
                  {env.data.length === 0
                    ? <Text tone="muted">{t('portal.partners.content.calendar.backlog.empty')}</Text>
                    : <ul className="qc-chips">{env.data.map((item) => <ItemChip key={item.id} item={item} movable={movable(item)} onMove={onMove} />)}</ul>}
                  <Link to={listHref({ ...shared, backlog: 'true' })}>{t('portal.partners.content.calendar.backlog.all')}</Link>
                </>
              )}
            </Loadable>
          </section>
        </aside>
      </div>
    </Screen>
  );
}

/**
 * One item. The title links to the item page. Movable items are draggable (not while their date form is open, since
 * inputs inside a draggable element are awkward in some browsers) and have the keyboard "Move to date" control.
 */
function ItemChip({ item, movable, onMove }: {
  item: DeliverableView; movable: boolean; onMove: (item: DeliverableView, date: string) => Promise<boolean>;
}) {
  const { t } = useSurface();
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(item.workingDate ?? '');
  const formId = useId();
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!date) return;
    if (await onMove(item, date)) setOpen(false);
  };
  return (
    <li className="qc-chip" data-testid="calendar-item" data-id={item.id} data-tone={statusTone(item.status)} data-movable={movable ? 'true' : 'false'}
      draggable={movable && !open}
      onDragStart={movable ? (e) => {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData(DRAG_TYPE, item.id);
        e.dataTransfer.setData('text/plain', item.id);
      } : undefined}>
      <Link to={`/content/items/${encodeURIComponent(item.id)}`} className="qc-chip__title">{item.title}</Link>
      <small>{item.clientName}</small>
      <StatusBadge status={item.status} />
      {movable ? (
        <>
          <Button type="button" aria-expanded={open} aria-controls={open ? formId : undefined}
            onClick={() => { setDate(item.workingDate ?? ''); setOpen((v) => !v); }}>
            {t('portal.partners.content.calendar.move.open')}
          </Button>
          {open ? (
            <form id={formId} className="qc-move" onSubmit={submit}>
              <label htmlFor={`${formId}-date`}>{t('portal.partners.content.calendar.move.label', { title: item.title })}</label>
              <input id={`${formId}-date`} type="date" name="date" value={date} required onChange={(e) => setDate(e.target.value)} />
              <Button type="submit">{t('portal.partners.content.calendar.move.save')}</Button>
              <Button type="button" onClick={() => setOpen(false)}>{t('portal.partners.content.calendar.move.cancel')}</Button>
            </form>
          ) : null}
        </>
      ) : null}
    </li>
  );
}
